import type { ConnectSpaceMode } from "@ando/core";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
  toast,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Bot, Loader2, Users2 } from "lucide-react";
import * as React from "react";
import { useCreateConnectSpace, usePatchConnectSpace } from "../hooks/useConnectMutations.js";

/**
 * CreateSpaceDialog — owner-side "Nuovo spazio" modal.
 *
 * Collects name + description + mode (collaborazione/chatbot). The BE
 * ``createSpace`` body only carries ``{ name, description }`` (mode defaults
 * server-side to ``collaboration``); when the user picks ``chatbot`` we issue
 * a follow-up ``patchSpace`` so the create flow stays DRY against the
 * existing endpoint contract without inventing a new request shape. On
 * success we route the caller into the new detail shell via ``onCreated``.
 *
 * Reuses the @ando/ui Dialog primitives + Button (same chrome as
 * ShareLinkDialog) — no bespoke modal. Mode toggle uses icon+text segmented
 * controls (scope/meaning never by colour alone).
 */

export interface CreateSpaceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Called with the new space id once creation (and optional mode patch) lands. */
  readonly onCreated?: (spaceId: string) => void;
}

export function CreateSpaceDialog({ open, onOpenChange, onCreated }: CreateSpaceDialogProps) {
  const { i18n } = useLingui();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [mode, setMode] = React.useState<ConnectSpaceMode>("collaboration");
  const create = useCreateConnectSpace();
  const patch = usePatchConnectSpace();
  const busy = create.isPending || patch.isPending;
  const nameRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setMode("collaboration");
      // Focus the name field once the dialog has mounted (no autoFocus attr).
      const t = window.setTimeout(() => nameRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const canSubmit = name.trim().length > 0 && !busy;

  const submit = React.useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    try {
      const space = await create.mutateAsync({
        name: trimmed,
        description: description.trim() || null,
      });
      // Mode defaults to collaboration server-side; only patch when chatbot.
      if (mode === "chatbot") {
        await patch.mutateAsync({ spaceId: space.id, patch: { mode } });
      }
      onOpenChange(false);
      onCreated?.(space.id);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : i18n._(msg`Impossibile creare lo spazio. Riprova.`),
      );
    }
  }, [name, description, mode, busy, create, patch, onOpenChange, onCreated, i18n]);

  const MODES: readonly {
    id: ConnectSpaceMode;
    label: string;
    hint: string;
    Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  }[] = [
    {
      id: "collaboration",
      label: i18n._(msg`Collaborazione`),
      hint: i18n._(msg`Inviti membri nominali che interrogano le fonti condivise.`),
      Icon: Users2,
    },
    {
      id: "chatbot",
      label: i18n._(msg`Chatbot`),
      hint: i18n._(msg`Un assistente pubblico, incorporabile, sulle stesse fonti.`),
      Icon: Bot,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Trans>Nuovo spazio Connect</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Crea uno spazio per condividere una porzione del tuo workspace con ospiti esterni.
              Sceglierai cosa condividere subito dopo.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="flex flex-col gap-1.5">
            <span className="font-medium text-[12px] text-ink-2">
              <Trans>Nome</Trans>
            </span>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder={i18n._(msg`Es. Due diligence — Acme S.p.A.`)}
              className={cn(
                "rounded-lg border border-hairline bg-white/70 px-3 py-2 text-[13px] text-ink-1 placeholder:text-ink-3",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/55",
              )}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="font-medium text-[12px] text-ink-2">
              <Trans>Descrizione</Trans>{" "}
              <span className="font-normal text-ink-2">
                <Trans>(facoltativa)</Trans>
              </span>
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={400}
              placeholder={i18n._(msg`A cosa serve questo spazio?`)}
              className={cn(
                "resize-none rounded-lg border border-hairline bg-white/70 px-3 py-2 text-[13px] text-ink-1 placeholder:text-ink-3",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/55",
              )}
            />
          </label>

          <fieldset className="flex flex-col gap-2">
            <legend className="font-medium text-[12px] text-ink-2">
              <Trans>Modalità</Trans>
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {MODES.map(({ id, label, hint, Icon }) => {
                const active = mode === id;
                return (
                  <label
                    key={id}
                    className={cn(
                      "flex cursor-pointer flex-col items-start gap-1 rounded-xl border p-3 text-left transition-colors duration-150 ease-out",
                      "focus-within:ring-2 focus-within:ring-ando-orange/55",
                      active
                        ? "border-ando-orange/60 bg-ando-orange-soft"
                        : "border-hairline bg-white/50 hover:bg-white/75",
                    )}
                  >
                    <input
                      type="radio"
                      name="connect-space-mode"
                      value={id}
                      checked={active}
                      onChange={() => setMode(id)}
                      className="sr-only"
                    />
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 font-semibold text-[12.5px]",
                        // Dark accent on the soft-orange active card clears AA
                        // (~5.9:1); bright orange-on-soft failed (~2.2:1).
                        active ? "text-warning-foreground" : "text-ink-1",
                      )}
                    >
                      <Icon size={14} strokeWidth={2.1} aria-hidden />
                      {label}
                    </span>
                    <span className="text-[11px] text-ink-2">{hint}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              <Trans>Annulla</Trans>
            </Button>
            <Button type="submit" variant="primary" size="md" disabled={!canSubmit}>
              {busy ? <Loader2 size={14} className="animate-spin" aria-hidden /> : null}
              <Trans>Crea spazio</Trans>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
CreateSpaceDialog.displayName = "CreateSpaceDialog";
